import type { AudioRecordingModelData } from "@/store/audio-recordings/types";
import {
  getMissingAudioContentFailureInfo,
  getPersistedSilenceSummary,
} from "@/store/audio-recordings/app-events";
import type { SilenceSnapshot } from "@/store/audio/recording/silence-detection";
import type { ValidationResult } from "@/store/audio/pipeline/audio-pipeline.types";

export const NO_USABLE_AUDIO_FOUND_MESSAGE =
  "No usable audio detected. Check your microphone or system audio, then record again.";
export const AUDIO_ARTIFACT_MISSING_MESSAGE =
  "Captured audio is missing for this recording. Record again to continue.";

/**
 * Hard gate that runs after recording finalize and before any upload or
 * backend retranscribe work. Classifies the finalized artifact as valid,
 * `no-usable-audio-found` (silent or too short), or `artifact-missing`.
 *
 * The silence verdict is taken as a snapshot of the in-process
 * silence-detection manager at the moment the recording transitions to
 * completed. We do not re-decode the blob — the live signal is trusted.
 */
export type FinalAudioValidationInput = {
  modelData: AudioRecordingModelData;
  hasPersistedArtifact: boolean;
  hasTranscriptEvidence: boolean;
  silenceSnapshot: SilenceSnapshot | null;
};

function shouldClassifyAsSilent({
  modelData,
  silenceSnapshot,
}: {
  modelData: AudioRecordingModelData;
  silenceSnapshot: SilenceSnapshot | null;
}): boolean {
  if (silenceSnapshot && silenceSnapshot.observedDurationMs > 0) {
    return !silenceSnapshot.hasEverDetectedSound;
  }

  const persistedSilenceSummary = getPersistedSilenceSummary({ modelData });
  return persistedSilenceSummary !== null && !persistedSilenceSummary.hasDetectedSound;
}

function hasDetectedSoundEvidence({
  modelData,
  silenceSnapshot,
}: {
  modelData: AudioRecordingModelData;
  silenceSnapshot: SilenceSnapshot | null;
}): boolean {
  if (silenceSnapshot && silenceSnapshot.observedDurationMs > 0) {
    return silenceSnapshot.hasEverDetectedSound;
  }

  const persistedSilenceSummary = getPersistedSilenceSummary({ modelData });
  return persistedSilenceSummary !== null && persistedSilenceSummary.hasDetectedSound;
}

/**
 * Classify a finalized recording. Callers should act on the result as
 * follows:
 * - `valid`                 → advance the pipeline to resolve-transcript-source
 * - `no-usable-audio-found` → terminate locally; user-facing "No usable audio found"
 * - `artifact-missing` → terminate locally; user-facing artifact error
 */
export function classifyFinalizedAudio(input: FinalAudioValidationInput): ValidationResult {
  const missingInfo = getMissingAudioContentFailureInfo({ modelData: input.modelData });
  const hasDetectedSound = hasDetectedSoundEvidence({
    modelData: input.modelData,
    silenceSnapshot: input.silenceSnapshot,
  });

  if (input.hasTranscriptEvidence) {
    return { kind: "valid" };
  }

  if (missingInfo.durationResult.success && missingInfo.isTooShort && !hasDetectedSound) {
    return { kind: "no-usable-audio-found", reason: "too-short" };
  }

  if (
    shouldClassifyAsSilent({
      modelData: input.modelData,
      silenceSnapshot: input.silenceSnapshot,
    })
  ) {
    return { kind: "no-usable-audio-found", reason: "silent" };
  }

  if (!input.hasPersistedArtifact) {
    return { kind: "artifact-missing" };
  }

  if (!missingInfo.durationResult.success) {
    // Duration can't be derived from app_events and we do not have another
    // local signal proving the recording was just silent/too short.
    return { kind: "artifact-missing" };
  }

  return { kind: "valid" };
}
